import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../../../axios/interceptor';
import openNotification from '../../../components/notification';

const initialState = {
    list: [],
    totalRecords: 0,
    loading: false,
    error: null,
    contract: null,
    dashboard: null,
    calendarEvents: [],
    categories: [],
    categoryOptions: [],
    customFields: [],
    isSuccess: false,
    isUpdating: false,
    aiExtraction: null,
    chatMessages: [],
    chatLoading: false,
};

export const slice = createSlice({
    name: 'contracts',
    initialState,
    reducers: {
        setContractList: (state, action) => {
            state.list = action.payload;
            state.isUpdating = false;
        },
        setTotalRecords: (state, action) => {
            state.totalRecords = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setChatLoading: (state, action) => {
            state.chatLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setContract: (state, action) => {
            state.contract = action.payload;
        },
        setDashboard: (state, action) => {
            state.dashboard = action.payload;
        },
        setCalendarEvents: (state, action) => {
            state.calendarEvents = action.payload;
        },
        setCategories: (state, action) => {
            state.categories = action.payload;
        },
        setCategoryOptions: (state, action) => {
            state.categoryOptions = action.payload;
        },
        setCustomFields: (state, action) => {
            state.customFields = action.payload;
        },
        setIsSuccess: (state, action) => {
            state.isSuccess = action.payload;
        },
        setIsUpdating: (state, action) => {
            state.isUpdating = action.payload;
        },
        setAiExtraction: (state, action) => {
            state.aiExtraction = action.payload;
        },
        addChatMessage: (state, action) => {
            state.chatMessages = [...state.chatMessages, action.payload];
        },
        setChatMessages: (state, action) => {
            state.chatMessages = action.payload;
        },
    },
});

export const getContracts = createAsyncThunk('getContracts', (filterData, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract/get', filterData).then((response) => {
        if (response.data.isSuccess) {
            dispatch(setContractList(response.data.records));
            dispatch(setTotalRecords(response.data.totalRecords));
        } else {
            dispatch(setContractList([]));
            dispatch(setTotalRecords(0));
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const getContract = createAsyncThunk('getContract', (id, { dispatch }) => {
    dispatch(setLoading(true));
    axios.get(`/contract?id=${id}`).then((response) => {
        if (response.data.isSuccess) {
            dispatch(setContract(response.data.record));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const createContract = createAsyncThunk('createContract', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract/insert', data.formData).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(setIsSuccess(true));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const updateContract = createAsyncThunk('updateContract', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract/update', data.formData).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(setIsSuccess(true));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const archiveContract = createAsyncThunk('archiveContract', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post(`/contract/archive?id=${data.id}`).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(getContracts(data.filterData));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const toggleContractStatus = createAsyncThunk('toggleContractStatus', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post(`/contract/status?id=${data.id}`).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(getContracts(data.filterData));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const getDashboardStats = createAsyncThunk('getDashboardStats', (organizer, { dispatch }) => {
    dispatch(setLoading(true));
    axios.get(`/contract/dashboard?organizer=${organizer}`).then((response) => {
        if (response.data.isSuccess) {
            const s = response.data.stats || {};
            dispatch(setDashboard({
                activeContracts: s.totalActive ?? 0,
                monthlyCost: s.monthlyTotal ?? 0,
                yearlyCost: s.yearlyTotal ?? 0,
                expiringIn30Days: s.expiringSoon30 ?? 0,
                noticeDeadlinesIn30Days: s.noticeDue30 ?? 0,
                autoRenewalCount: s.autoRenewalCount ?? 0,
                topContracts: s.topExpensive ?? [],
                costByCategory: (s.costByCategory ?? []).map(c => ({
                    category: c.categoryName,
                    color: c.categoryColor,
                    totalCost: c.monthlyCost,
                    count: c.count,
                })),
                missingDataContracts: s.missingDataContracts ?? [],
            }));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const getCalendarEvents = createAsyncThunk('getCalendarEvents', (organizer, { dispatch }) => {
    dispatch(setLoading(true));
    axios.get(`/contract/calendar?organizer=${organizer}`).then((response) => {
        if (response.data.isSuccess) {
            dispatch(setCalendarEvents(response.data.records));
        } else {
            dispatch(setCalendarEvents([]));
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const getCategories = createAsyncThunk('getCategories', (filterData, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract-category/get', filterData).then((response) => {
        if (response.data.isSuccess) {
            dispatch(setCategories(response.data.records));
        } else {
            dispatch(setCategories([]));
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const getCategoryOptions = createAsyncThunk('getCategoryOptions', (organizer, { dispatch }) => {
    axios.get(`/contract-category/options?organizer=${organizer}`).then((response) => {
        if (response.data.isSuccess) {
            dispatch(setCategoryOptions(response.data.records));
        } else {
            dispatch(setCategoryOptions([]));
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    });
});

export const createCategory = createAsyncThunk('createCategory', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract-category/insert', data.categoryData).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(getCategories({ organizer: data.organizer }));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const updateCategory = createAsyncThunk('updateCategory', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract-category/update', data.categoryData).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(getCategories({ organizer: data.organizer }));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const getCustomFields = createAsyncThunk('getCustomFields', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract-custom-field/get', data).then((response) => {
        if (response.data.isSuccess) {
            dispatch(setCustomFields(response.data.records));
        } else {
            dispatch(setCustomFields([]));
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const createCustomField = createAsyncThunk('createCustomField', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract-custom-field/insert', data.fieldData).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(getCustomFields({ category: data.category, organizer: data.organizer }));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const uploadExtract = createAsyncThunk('uploadExtract', (data, { dispatch }) => {
    dispatch(setLoading(true));
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.organizer) formData.append('organizer', data.organizer);
    axios.post('contract-ai/upload-extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    }).then((response) => {
        if (response.data.isSuccess) {
            dispatch(setAiExtraction({
                extractionId: response.data.extractionId,
                extractedData: response.data.extractedData,
                confidence: response.data.confidence,
            }));
        } else {
            openNotification({ message: response.data.message || 'Fehler bei der KI-Import.', type: 'error' });
        }
    }).catch((error) => {
        const msg = error.response?.data?.message || error.message || 'Verbindungsfehler.';
        openNotification({ message: msg, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const confirmExtraction = createAsyncThunk('confirmExtraction', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/contract-ai/confirm', data).then((response) => {
        if (response.data.isSuccess) {
            openNotification({ message: response.data.message, type: 'success' });
            dispatch(setIsSuccess(true));
        } else {
            openNotification({ message: response.data.message || 'Fehler beim Speichern.', type: 'error' });
        }
    }).catch((error) => {
        const msg = error.response?.data?.message || error.message;
        openNotification({ message: msg, type: 'error' });
    }).finally(() => {
        dispatch(setLoading(false));
    });
});

export const sendChatMessage = createAsyncThunk('sendChatMessage', (data, { dispatch }) => {
    dispatch(setChatLoading(true));
    dispatch(addChatMessage({ role: 'user', content: data.message, timestamp: new Date().toISOString() }));
    axios.post('/contract-chat/chat', { message: data.message, organizer: data.organizer }).then((response) => {
        if (response.data.isSuccess) {
            dispatch(addChatMessage({
                role: 'assistant',
                content: response.data.answer,
                contracts: response.data.supportingData?.contracts || [],
                timestamp: new Date().toISOString(),
            }));
        } else {
            openNotification({ message: response.data.message, type: 'error' });
        }
    }).catch((error) => {
        openNotification({ message: error.message, type: 'error' });
    }).finally(() => {
        dispatch(setChatLoading(false));
    });
});

export const {
    setContractList,
    setTotalRecords,
    setLoading,
    setChatLoading,
    setError,
    setContract,
    setDashboard,
    setCalendarEvents,
    setCategories,
    setCategoryOptions,
    setCustomFields,
    setIsSuccess,
    setIsUpdating,
    setAiExtraction,
    addChatMessage,
    setChatMessages,
} = slice.actions;

export default slice.reducer;
